import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConversation } from '../../contexts/ConversationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { LensContext } from '../../contexts/LensContext';
import { buildSendMetadata, resolveWriteTarget, MessagesByLens } from '../../contexts/lensWriteTarget';
import { ForkComposePill } from './ForkComposePill';
import { resolveCursorTarget } from '../../contexts/replyCursor';
import { pendingForSpine, pendingForTrail, morphState } from '../../contexts/pendingReplies';
import { trailResidents } from '../../contexts/trailResidents';
import { MessageList } from './MessageList';
import { findForkPoints, buildChildrenByParent } from './childrenByParent';
import { forksExpandedFromLens, forkFocusLens } from './forkLensDrive';
import type { PhaseEvent } from '../../hooks/usePersonaToolProgress';
import type { PeerMetadata } from '../../types/council';
import * as modelService from '../../services/modelService';
import { ModelPicker } from '../models/ModelPicker';
import { SendToMenu } from './SendToMenu';
import ComposeMenu from './ComposeMenu';
import { SquadBannerStack } from './SquadBannerStack';
import { MultiLensPicker } from './composePanel/MultiLensPicker';

// Auto-fence pasted content above either threshold so a dropped log /
// JSON / code blob doesn't unfurl as running paragraphs in the markdown
// renderer. Numbers picked to feel "obviously big" — a typed paragraph
// is well under both, an accidental log dump trips both.
const PASTE_FENCE_CHAR_THRESHOLD = 500;
const PASTE_FENCE_LINE_THRESHOLD = 5;

interface ConversationInterfaceProps {
  title: string;
  subtitle?: string;
  avatarName?: string;
  avatarSpecialization?: string;
  avatarUrl?: string;
  className?: string;
  sessionId?: string;
  personaId?: string;
  personaName?: string;
  /** Hide the built-in header when parent provides its own */
  hideHeader?: boolean;
  /** Show only the toolbar (model + send-to) without avatar/title/back */
  compactHeader?: boolean;
  /** Status text to show alongside loading dots */
  loadingStatus?: string;
  /** Structured phase-event stream from the persona chat progress hook.
   *  Drives the expandable phase trail above the pending assistant message —
   *  one record per tool/peer/heartbeat transition, so individual terminal
   *  transitions survive even when aggregate views are displayed. */
  phaseEvents?: PhaseEvent[];
  /** Auto-focus the textarea on mount + after a resumed session finishes
   *  loading (two-phase per Amendment-A2). Trailhead passes true; legacy
   *  PersonaChat omits to preserve no-jump behavior. */
  autoFocus?: boolean;
  /** Council Trail v0.1 — per-message peer-author metadata from
   *  useMessagesPeerMetadata. Undefined / empty for Solo Trail sessions;
   *  forwarded as-is to MessageList. */
  peerMetadataByMessageId?: ReadonlyMap<string, PeerMetadata>;
  /** Council Trail v0.1 — active persona Set from LensContext. Null =
   *  no provider / canopy mode = no off-lens dimming. */
  lensSnapshot?: import('../../utils/lensVisibility').LensSnapshot | null;
}

export const ConversationInterface: React.FC<ConversationInterfaceProps> = ({
  title,
  subtitle,
  avatarName,
  avatarSpecialization,
  avatarUrl,
  className = '',
  sessionId,
  personaId,
  personaName,
  hideHeader = false,
  compactHeader = false,
  loadingStatus,
  phaseEvents,
  autoFocus = false,
  peerMetadataByMessageId,
  lensSnapshot,
}) => {
  const { state, sendMessage, sendSpineReply, sendFork, pendingReplies, registerInsertHandler, notifyTextareaActivity } = useConversation();
  const { darkMode } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const lensCtx = useContext(LensContext);
  const [currentModel, setCurrentModel] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [showModelDropdown, setShowModelDropdown] = useState<boolean>(false);
  const [composeText, setComposeText] = useState('');
  // SP3 H.3 — picker selection for multi-lens write target.
  const [pickerSelection, setPickerSelection] = useState<string | null>(null);

  // SP3 H.3 — derive messagesByLens for resolveWriteTarget. State.messages
  // is the linear chat list; we bucket peer-authored messages by
  // persona_id for the `persona:<pid>` keys lensWriteTarget expects.
  // Trail mode is best-effort in v0 (no trail-id mapping in state.messages
  // → trail bucket stays empty; backend falls through to redundant-
  // explicit-leaf default per chat.py dc1-lite validator).
  const messagesByLens: MessagesByLens = useMemo(() => {
    const buckets: MessagesByLens = {};
    for (const m of state.messages) {
      const pid = (m.metadata as any)?.peer_persona_id
        || (m.metadata as any)?.persona_id;
      if (pid) {
        const k = `persona:${pid}`;
        if (!buckets[k]) buckets[k] = [];
        buckets[k].push({
          messageId: m.id,
          personaId: pid,
          // 2026-06-12 — thread the envelope's trail + display name through
          // so the write target can send a gate-legal trail_id ("Replying
          // on X's trail" must MEAN a trail send) and the pill copy shows
          // a name, not a uuid.
          trailId: (m.metadata as any)?.trail_id ?? null,
          personaName: (m.metadata as any)?.persona_display_name_at_join,
        });
      }
    }
    return buckets;
  }, [state.messages]);

  // Reply-targeting §3 — resolve the operator's reply cursor to its spine.
  // Stale/unknown cursor self-heals to null (no target).
  const cursorTarget = useMemo(
    () => resolveCursorTarget(lensCtx?.cursorTurnId ?? null, state.messages as any[]),
    [lensCtx?.cursorTurnId, state.messages],
  );

  // SP3 H.3 — Lens-driven write-target. Skips resolution when LensContext
  // is absent (Solo Trail sessions outside the LensProvider). Cursor (when
  // set) outranks lens inference — resolveWriteTarget's top branch.
  const writeTarget = useMemo(() => {
    if (!lensCtx) return null;
    return resolveWriteTarget(lensCtx, messagesByLens, cursorTarget, personaName);
  }, [lensCtx, messagesByLens, cursorTarget, personaName]);

  // Pending replies for the CURRENT cursor target — drives the pill morph
  // + the queued sub-copy. P2: trail-keyed (co-residents camp on their own
  // spines) and TRANSPORT-gated — a main-spine cursor carries a trailId too,
  // and an unguarded trail lookup would let a main-trail pending morph a
  // "Replying to <route persona>" pill into a lie.
  const spinePending = useMemo(
    () => (writeTarget?.sendVia === 'spine_reply'
      ? (writeTarget.trailId
          ? pendingForTrail(pendingReplies ?? [], writeTarget.trailId)
          : pendingForSpine(pendingReplies ?? [], writeTarget.spineId ?? null))
      : []),
    [pendingReplies, writeTarget?.sendVia, writeTarget?.trailId, writeTarget?.spineId],
  );
  const morph = useMemo(
    () => morphState(spinePending, cursorTarget?.residentName ?? null),
    [spinePending, cursorTarget?.residentName],
  );

  // §3 (F6) — bubble re-roll handshake. The bubble sets {cursorTurnId: M, cursorMode:
  // 'fork', cursorReroll: true}; here (where cursorTarget resolves M→spine and sendFork
  // live) we fire an IMMEDIATE reroll fork, then clear the intent. Steer (with a prompt)
  // goes through the compose bar instead.
  useEffect(() => {
    if (!lensCtx?.cursorReroll) return;
    if (cursorTarget && !cursorTarget.isMainSpine && cursorTarget.spineRootMessageId) {
      sendFork('', {
        spineId: cursorTarget.spineRootMessageId,
        fromMessageId: cursorTarget.cursorTurnId,
        mode: 'reroll',
        residentName: cursorTarget.residentName ?? null,
      });
    }
    lensCtx.setLens({ cursorTurnId: null, cursorMode: 'reply', cursorReroll: false });
  }, [lensCtx?.cursorReroll, cursorTarget, sendFork, lensCtx]);

  // sp3 — fork topology for the left-panel tree-view (reuses the pure builder).
  const forkData = useMemo(() => ({
    forks: findForkPoints(state.messages),
    childrenByParent: buildChildrenByParent(state.messages),
  }), [state.messages]);
  // Expand default rides the lens; selecting a branch focuses that peer's trail
  // so the existing resolveWriteTarget machinery targets it (no second selector).
  const forksExpandedByDefault = forksExpandedFromLens(lensCtx?.mode);
  const onFocusBranch = useCallback(
    (b: { personaId: string; turnId: string }) => {
      lensCtx?.setLens(forkFocusLens(b.personaId));
    }, [lensCtx]);

  // Compute the effective parent_ids based on writeTarget + picker
  // selection (multi-mode operator override).
  const _composeAndSend = useCallback((msg: string) => {
    // Reply-targeting §3 — cursor on a companion spine rides the spine-reply
    // route. Sticky cursor: the selection persists for the next send (spec
    // decision 8); Escape / pill × clears it.
    // §3 fork — cursor on a companion spine in fork mode branches a NEW spine from M.
    if (writeTarget?.sendVia === 'fork' && writeTarget.spineId && writeTarget.forkFromMessageId) {
      sendFork(msg, {
        spineId: writeTarget.spineId,
        fromMessageId: writeTarget.forkFromMessageId,
        mode: 'steer',
        residentName: cursorTarget?.residentName ?? null,
      });
      lensCtx?.setLens({ cursorTurnId: null, cursorMode: 'reply' }); // fork sent → exit fork mode
      return;
    }
    if (writeTarget?.sendVia === 'spine_reply' && writeTarget.spineId) {
      // P2: name map for the ack's per-resident dispatched[] entries.
      const residentsByPersona = Object.fromEntries(
        trailResidents(state.messages as any, writeTarget.trailId ?? null)
          .filter((r) => r.personaId)
          .map((r) => [r.personaId as string, r.name]),
      );
      sendSpineReply(msg, writeTarget.spineId, residentsByPersona);
      return;
    }
    // SP3 H.3 regression fix (May 18 hotfix): only attach parent_ids
    // for non-canopy lens modes (pill or picker affordance). Canopy
    // mode previously computed parentIds = [latest message in FE state],
    // which diverges from the backend's session tip when peer-authored
    // spine roots have landed via WS but the FE state is mid-update —
    // the backend's _validate_parent_ids_dc1_lite (chat.py:268) then
    // rejects with 422 invalid_parent and the operator's send silently
    // fails. Restoring legacy canopy behavior (no parent_ids → backend
    // defaults to redundant-explicit-leaf) preserves the chip/smoke-test
    // flow while keeping the write-target plumbing for single/trail/multi.
    let parentIds: string[] = [];
    // 2026-06-12 — the write target threads the TARGET's trail (single's
    // peer trail, the pinned trail, the picked multi-option's trail).
    // buildSendMetadata prefers it: trail sends are gate-legal where
    // non-tip parent_ids are not ("Replying on X's trail" now IS one).
    // Null trail (pre-SP1 envelopes) → parent_ids path unchanged.
    let trailId: string | null = writeTarget?.trailId ?? null;
    if (writeTarget?.affordance === 'picker' && pickerSelection) {
      const opt = writeTarget.pickerOptions?.find(
        (o) => o.personaId === pickerSelection);
      if (opt?.latestTurnId) parentIds = [opt.latestTurnId];
      trailId = opt?.trailId ?? null;
    } else if (writeTarget?.affordance === 'pill') {
      parentIds = writeTarget.parentIds || [];
    }
    const metadata = buildSendMetadata(parentIds, trailId);
    sendMessage(msg, metadata);
    // Sticky lastMultiTarget — write through to the lens context so the
    // next send remembers the selection.
    if (pickerSelection && lensCtx) {
      lensCtx.setLens({ lastMultiTarget: pickerSelection });
    }
  }, [writeTarget, pickerSelection, sendMessage, sendSpineReply, sendFork, cursorTarget, lensCtx, state.messages]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Debounces notifyTextareaActivity to once per ~50ms window even under
  // fast typing — subscribers (e.g. WorkshopRow) react to "the user is
  // composing" without a keystroke storm.
  const textareaActivityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cursor-aware insert. Drops `text` at the textarea's caret (or
  // replaces the current selection), keeping any draft the user has
  // already typed intact. The previous behavior — `setComposeText(text)`
  // wholesale — silently ate user-typed content the moment a chip or
  // template fired. rAF callback restores focus + caret position + the
  // auto-resize height after React commits the new value, so the
  // user never has to re-click into the textarea after picking from
  // ComposeMenu.
  const insertAtCursor = useCallback(
    (text: string) => {
      const ta = textareaRef.current;
      if (!ta) {
        setComposeText((prev) => prev + text);
        return;
      }
      const start = ta.selectionStart ?? composeText.length;
      const end = ta.selectionEnd ?? composeText.length;
      const next = composeText.slice(0, start) + text + composeText.slice(end);
      setComposeText(next);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        const pos = start + text.length;
        el.setSelectionRange(pos, pos);
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 120) + 'px';
      });
    },
    [composeText],
  );

  // Register the cursor-aware insertAtCursor with ConversationContext so
  // ComposeMenu (now relocated to ComposePanel / WorkshopRow at the top
  // of the surface) can request insertions without prop-drilling. The textarea
  // owner stays here; only the insertion entry point is exposed via
  // context. Re-registers when insertAtCursor changes (composeText dep)
  // so the active handler always closes over current text state.
  useEffect(() => {
    return registerInsertHandler(insertAtCursor);
  }, [insertAtCursor, registerInsertHandler]);

  // Auto-focus the textarea (two-phase per Amendment-A2). Phase 1 fires
  // on fresh-mount (new sessions). Phase 2 re-fires when a resumed session
  // finishes loading — without it, the message-list paint shifts layout
  // and the first focus call hits a stale offset, leaving the textarea
  // off-screen.
  useEffect(() => {
    if (autoFocus) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [autoFocus]);
  useEffect(() => {
    if (autoFocus && state.status === 'idle') {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [autoFocus, state.status]);

  // Big-paste auto-fence. If the pasted blob crosses either threshold AND
  // the caret isn't already inside an open ``` fence, wrap the paste in
  // a fenced code block so the markdown renderer treats it as a code
  // chunk instead of unfurling it. One-tick info toast tells the user
  // what happened so the auto-wrap doesn't feel like ghost editing.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pasted = e.clipboardData.getData('text');
      if (!pasted) return;
      const charCount = pasted.length;
      const lineCount = pasted.split('\n').length;
      const isLarge =
        charCount > PASTE_FENCE_CHAR_THRESHOLD ||
        lineCount > PASTE_FENCE_LINE_THRESHOLD;
      if (!isLarge) return;
      const ta = e.currentTarget;
      const before = composeText.slice(0, ta.selectionStart ?? 0);
      const fenceCount = (before.match(/```/g) ?? []).length;
      if (fenceCount % 2 === 1) return; // already inside an open fence
      e.preventDefault();
      const wrapped = `\n\`\`\`\n${pasted.replace(/\n+$/, '')}\n\`\`\`\n`;
      insertAtCursor(wrapped);
      showToast(
        `Pasted ${charCount.toLocaleString()} chars · auto-wrapped as code block`,
        'info',
      );
    },
    [composeText, insertAtCursor, showToast],
  );

  // Fetch the current model being used for this session
  useEffect(() => {
    if (sessionId) {
      const fetchModel = async () => {
        try {
          const modelData = await modelService.getDefaultModel('session', sessionId);
          // Response shape has two variants:
          //   with preference:   { preference: { model_id, ... }, model: { id, ... } }
          //   system default:    { model: { id, ... }, is_system_default: true }
          // Previous code read modelData.model_id (a field that doesn't exist on
          // either shape), so currentModel was always undefined.
          const modelId =
            modelData?.preference?.model_id ??
            modelData?.model?.id ??
            null;
          setCurrentModel(modelId);
        } catch (error) {
          console.error('Failed to fetch model:', error);
          // If we can't fetch the specific model, use the default
          setCurrentModel(null); // Let the backend decide via model preference lookup
        }
      };

      fetchModel();
    }
  }, [sessionId]);

  // Fetch available models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const models = await modelService.getModels();
        setAvailableModels(models);
      } catch (error) {
        console.error('Failed to fetch models:', error);
      }
    };
    
    fetchModels();
  }, []);

  // Handle model change
  const handleModelChange = async (modelId: string) => {
    if (sessionId) {
      try {
        await modelService.setModelPreference('session', sessionId, modelId);
        setCurrentModel(modelId);
        setShowModelDropdown(false);
      } catch (error) {
        console.error('Failed to change model:', error);
      }
    }
  };


  return (
    <div className={`flex flex-col h-full ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} ${className}`}>
      {/* Header — full, compact (tools only), or hidden */}
      {!hideHeader && (
        <div className={`px-3 py-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center shrink-0`}>
          {!compactHeader && (
            <div className="flex items-center">
              <button
                onClick={() => navigate('/chat/sessions')}
                className={`mr-3 p-2 rounded-full transition-colors ${
                  darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                }`}
                aria-label="Back to chats"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              <div className="flex items-center">
                {avatarName && (
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium mr-3 ${
                    darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {avatarName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h2 className="font-semibold text-lg">{title}</h2>
                  {subtitle && <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</p>}
                </div>
              </div>
            </div>
          )}
          {compactHeader && <div />}
          {/* Toolbar: Send To (model picker moved to footer) */}
          {!compactHeader && (
            <div className="flex items-center space-x-2">
              {sessionId && personaId && personaName && (
                <SendToMenu
                  sessionId={sessionId}
                  personaId={personaId}
                  personaName={personaName}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ChatDAGRAGDAG Sub-plan 3 Task F.4 — squad banners (shrink-0
          sibling between header and MessageList per SP3-MF-07; the
          parent's `flex flex-col h-full` chain keeps MessageList
          growable as the banner stack flexes). */}
      {sessionId && <SquadBannerStack sessionId={sessionId} />}

      <MessageList
        messages={state.messages}
        isLoading={state.status === 'loading'}
        loadingStatus={loadingStatus}
        phaseEvents={phaseEvents}
        currentModel={currentModel}
        sessionId={sessionId}
        personaId={personaId}
        personaName={personaName}
        peerMetadataByMessageId={peerMetadataByMessageId}
        lensSnapshot={lensSnapshot}
        forkData={forkData}
        forksExpandedByDefault={forksExpandedByDefault}
        onFocusBranch={onFocusBranch}
      />

      {/* ── Single-decker Compose Bar ── */}
      <div className={`px-3 py-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} shrink-0`}>
        {/* Reply-targeting §3 — cursor pill. Visually DISTINCT from the lens
            pill (📍 + violet vs muted text) so which targeting system is in
            charge reads in one second. Morphs into the resident-thinking
            state while a reply is pending on the selected spine. */}
        {writeTarget?.affordance === 'cursor-pill' && (
          <div
            data-reply-cursor-pill
            className={`flex items-center gap-1.5 text-xs mb-1 px-2 py-0.5 rounded-full w-fit ${
              darkMode ? 'bg-violet-900/40 text-violet-200' : 'bg-violet-100 text-violet-800'
            }`}
          >
            <span aria-hidden>📍</span>
            <span>
              {morph ? morph.copy : writeTarget.pillCopy}
              {morph?.queued && ' · queued behind previous reply'}
            </span>
            <button
              type="button"
              aria-label="Clear reply target"
              onClick={() => lensCtx?.setLens({ cursorTurnId: null })}
              className={`ml-0.5 rounded px-1 ${
                darkMode ? 'hover:bg-violet-800' : 'hover:bg-violet-200'
              }`}
            >
              ×
            </button>
          </div>
        )}
        {writeTarget?.affordance === 'fork-pill' && (
          <ForkComposePill
            pillCopy={writeTarget.pillCopy}
            darkMode={darkMode}
            onCancel={() => lensCtx?.setLens({ cursorTurnId: null, cursorMode: 'reply' })}
          />
        )}
        {/* SP3 H.3 — Lens-driven write-target affordance:
            pill copy for single/trail modes, picker for multi mode. */}
        {writeTarget?.affordance === 'pill' && writeTarget.pillCopy && (
          <div
            data-write-target-pill
            className={`text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}
          >
            {writeTarget.pillCopy}
          </div>
        )}
        {writeTarget?.affordance === 'picker' && writeTarget.pickerOptions && (
          <MultiLensPicker
            pickerOptions={writeTarget.pickerOptions}
            selectedPersonaId={
              pickerSelection
              || lensCtx?.lastMultiTarget
              || writeTarget.pickerOptions[0]?.personaId
              || ''
            }
            onSelect={setPickerSelection}
          />
        )}
        <div className="flex items-center gap-2">
          {/* Tools: Model + SendTo + Compose */}
          <ModelPicker
            entityId={sessionId || ''}
            value={currentModel}
            onModelSelect={handleModelChange}
            className="z-20"
          />
          {sessionId && personaId && personaName && (
            <SendToMenu
              sessionId={sessionId}
              personaId={personaId}
              personaName={personaName}
            />
          )}

          <ComposeMenu
            personaId={personaId}
            personaName={personaName}
            sessionId={sessionId}
            onInsert={insertAtCursor}
          />

          {/* Input + Send. Textarea is NOT disabled while loading — the
              user can compose the next message while the assistant
              works. Send is still gated on idle so we don't double-fire.
              Form-submit + Enter both bail silently when loading. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const msg = (composeText || '').trim();
              if (!msg || state.status === 'loading') return;
              _composeAndSend(msg);
              setComposeText('');
              if (textareaRef.current) textareaRef.current.style.height = 'auto';
            }}
            className="flex flex-1 gap-0 items-end"
          >
            <textarea
              ref={textareaRef}
              value={composeText}
              onChange={(e) => {
                setComposeText(e.target.value);
                // Auto-resize on every keystroke. Reset-to-auto first
                // so deleting back down to a single line contracts the
                // height instead of holding the previously-grown row.
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                // Debounced notifyTextareaActivity for workshop-rail close
                // (v0.4 §4.2 Amendment-A3).
                if (textareaActivityDebounceRef.current) {
                  clearTimeout(textareaActivityDebounceRef.current);
                }
                textareaActivityDebounceRef.current = setTimeout(() => {
                  notifyTextareaActivity();
                }, 50);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const msg = (composeText || '').trim();
                  if (!msg || state.status === 'loading') return;
                  _composeAndSend(msg);
                  setComposeText('');
                  (e.target as HTMLTextAreaElement).style.height = 'auto';
                }
                // Reply-targeting §3 — Escape clears the reply cursor.
                if (e.key === 'Escape' && lensCtx?.cursorTurnId) {
                  e.preventDefault();
                  lensCtx.setLens({ cursorTurnId: null, cursorMode: 'reply' });
                }
              }}
              onPaste={handlePaste}
              placeholder={
                personaName
                  ? `Message ${personaName.replace(/_/g, ' ')}…`
                  : 'Type your message…'
              }
              rows={1}
              className={`flex-1 px-3 py-1.5 rounded-l-lg text-sm border focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none overflow-hidden transition-shadow ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
            <button
              type="submit"
              disabled={state.status === 'loading' || !composeText?.trim()}
              className={`px-4 py-1.5 rounded-r-lg text-sm font-medium transition-colors self-stretch ${
                darkMode ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-emerald-500 hover:bg-emerald-600'
              } text-white ${state.status === 'loading' || !composeText?.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
